import { AnimatePresence, motion } from 'framer-motion'
import { ArrowLeft, CheckCircle, Eye, EyeOff, Mail, Shield } from 'lucide-react'
import { useState } from 'react'
import {
  changePassword,
  requestPasswordCode,
  verifycode,
} from '../../api/authApi'
import { toast } from '../../lib/toast'
import { useTheme, useTextTheme, useInputTheme, useStatusTheme } from '../../theme/hooks'
import CodeInput from '../shared/CodeInput'
import CodeTimer from '../shared/CodeTimer'
import PasswordStrength from '../shared/PasswordStrength'

type ForgotPasswordProps = {
  onBack: () => void
}

type FormErrors = {
  email?: string
  code?: string
  newPassword?: string
  confirmPassword?: string
}

const ForgotPassword = ({ onBack }: ForgotPasswordProps) => {
  const { theme } = useTheme()
  const textTheme = useTextTheme()
  const inputTheme = useInputTheme()
  const statusErrorTheme = useStatusTheme('error')

  const [currentStep, setCurrentStep] = useState<number>(1) // 1: email, 2: code, 3: password
  const [email, setEmail] = useState<string>('')
  const [verifiedCode, setVerifiedCode] = useState<string>('')
  const [newPassword, setNewPassword] = useState<string>('')
  const [confirmPassword, setConfirmPassword] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [errors, setErrors] = useState<FormErrors>({})
  const [focusedInput, setFocusedInput] = useState<string>('')
  const [passwordStrength, setPasswordStrength] = useState<number>(0)
  const [codeSentAt, setCodeSentAt] = useState<Date | null>(null)
  const [showPassword, setShowPassword] = useState<boolean>(false)

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const validatePassword = (password: string): boolean => {
    return password.length >= 8
  }

  const handleEmailSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setLoading(true)
    setErrors({})

    if (!email.trim()) {
      setErrors({ email: "L'email est requis" })
      toast.error("L'email est requis")
      setLoading(false)
      return
    }

    if (!validateEmail(email)) {
      setErrors({ email: 'Veuillez entrer un email valide' })
      toast.error('Veuillez entrer un email valide')
      setLoading(false)
      return
    }

    try {
      await requestPasswordCode(email)
      toast.success('Code de réinitialisation envoyé à votre email')
      setCodeSentAt(new Date())
      setCurrentStep(2)
    } catch (error: any) {
      console.error('Error requesting password code:', error)
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Erreur lors de l'envoi du code"
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleCodeComplete = async (codeString: string) => {
    setLoading(true)
    setErrors({})

    try {
      await verifycode(email, codeString)
      setVerifiedCode(codeString) // Store the verified code
      toast.success('Code vérifié avec succès')
      setCurrentStep(3)
    } catch (error: any) {
      console.error('Error verifying code:', error)
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        'Code invalide'
      toast.error(errorMessage)
      setErrors({ code: errorMessage })
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setLoading(true)
    setErrors({})

    if (!newPassword.trim()) {
      setErrors({ newPassword: 'Le nouveau mot de passe est requis' })
      toast.error('Le nouveau mot de passe est requis')
      setLoading(false)
      return
    }

    if (!validatePassword(newPassword)) {
      setErrors({
        newPassword: 'Le mot de passe doit contenir au moins 8 caractères',
      })
      toast.error('Le mot de passe doit contenir au moins 8 caractères')
      setLoading(false)
      return
    }

    if (newPassword !== confirmPassword) {
      setErrors({ confirmPassword: 'Les mots de passe ne correspondent pas' })
      toast.error('Les mots de passe ne correspondent pas')
      setLoading(false)
      return
    }

    try {
      await changePassword(email, verifiedCode, newPassword)
      toast.success('Mot de passe modifié avec succès')
      setCodeSentAt(null)
      setTimeout(() => {
        onBack()
      }, 1500)
    } catch (error: any) {
      console.error('Error changing password:', error)
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        'Erreur lors de la modification du mot de passe'
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const isInputActive = (field: string): boolean => {
    return (
      focusedInput === field ||
      (field === 'email' && !!email) ||
      (field === 'newPassword' && !!newPassword) ||
      (field === 'confirmPassword' && !!confirmPassword)
    )
  }

  const getStepIcon = () => {
    switch (currentStep) {
      case 1:
        return <Mail className="h-6 w-6" />
      case 2:
        return <Shield className="h-6 w-6" />
      case 3:
        return <CheckCircle className="h-6 w-6" />
      default:
        return <Mail className="h-6 w-6" />
    }
  }

  const getStepTitle = () => {
    switch (currentStep) {
      case 1:
        return 'Mot de passe oublié ?'
      case 2:
        return 'Vérification du code'
      case 3:
        return 'Nouveau mot de passe'
      default:
        return 'Mot de passe oublié ?'
    }
  }

  const getStepSubtitle = () => {
    switch (currentStep) {
      case 1:
        return 'Pas de souci, nous vous enverrons les instructions de réinitialisation.'
      case 2:
        return `Entrez le code à 6 chiffres envoyé à ${email}`
      case 3:
        return 'Définissez votre nouveau mot de passe'
      default:
        return 'Pas de souci, nous vous enverrons les instructions de réinitialisation.'
    }
  }

  const getInputBorderColor = (field: string, hasError: boolean): string => {
    if (hasError) return statusErrorTheme.border
    if (isInputActive(field)) return theme.brand.gold
    return inputTheme.border
  }

  const getInputBgColor = (field: string): string => {
    if (isInputActive(field)) return inputTheme.bgFocus
    return inputTheme.bg
  }

  return (
    <div className="w-full max-w-md">
      {/* Header with back button */}
      <div className="mb-8">
        <button
          onClick={onBack}
          className="mb-4 flex h-9 items-center transition-colors duration-200"
          style={{ color: textTheme.secondary }}
          onMouseEnter={(e) => { e.currentTarget.style.color = textTheme.primary }}
          onMouseLeave={(e) => { e.currentTarget.style.color = textTheme.secondary }}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Retour à la connexion
        </button>

        <div className="mb-4 flex items-center justify-center">
          <div
            className="flex h-12 w-12 items-center justify-center rounded-full"
            style={{ backgroundColor: 'rgba(246, 169, 69, 0.18)', color: theme.brand.accent.text }}
          >
            {getStepIcon()}
          </div>
        </div>

        <h1 className="mb-2 text-center text-3xl font-bold" style={{ color: textTheme.primary }}>
          {getStepTitle()}
        </h1>
        <p className="text-center" style={{ color: textTheme.secondary }}>{getStepSubtitle()}</p>
      </div>

      {/* Progress indicator */}
      <div className="mb-8 flex justify-center">
        <div className="flex space-x-2">
          {[1, 2, 3].map(step => (
            <div
              key={step}
              className="h-2 w-8 rounded-full transition-all duration-300"
              style={{
                backgroundColor: step <= currentStep
                  ? theme.brand.gold
                  : theme.background.tertiary,
              }}
            />
          ))}
        </div>
      </div>

      {/* Form content with animations */}
      {/* the login card zooms the panel in, so only step changes slide */}
      <AnimatePresence mode="wait" initial={false}>
        {currentStep === 1 && (
          <motion.form
            key="email-step"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
            onSubmit={handleEmailSubmit}
          >
            <div className="relative">
              <div
                className={`form-field relative transition-all duration-300 ${errors.email ? 'shake' : ''}`}
              >
                <input
                  id="email"
                  type="email"
                  className="w-full rounded-lg border px-4 py-3 transition-all duration-300 focus:outline-none"
                  style={{
                    borderColor: getInputBorderColor('email', !!errors.email),
                    backgroundColor: getInputBgColor('email'),
                    color: inputTheme.text,
                  }}
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  onFocus={() => setFocusedInput('email')}
                  onBlur={() => setFocusedInput('')}
                  placeholder=" "
                />
                <label
                  htmlFor="email"
                  className={`pointer-events-none absolute px-1 transition-all duration-300 ${
                    isInputActive('email')
                      ? '-top-2 left-3 z-10 text-xs'
                      : 'left-4 top-3 text-base'
                  }`}
                  style={{
                    color: isInputActive('email') ? theme.brand.accent.text : textTheme.tertiary,
                    // paint only the lower half so the label matches the field below the border
                    background: isInputActive('email') ? `linear-gradient(transparent 50%, ${inputTheme.bgFocus} 50%)` : 'transparent',
                  }}
                >
                  Email
                </label>
                {errors.email && (
                  <p className="mt-1 text-sm" style={{ color: statusErrorTheme.text }}>{errors.email}</p>
                )}
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`relative flex w-full justify-center overflow-hidden rounded-lg border border-transparent px-4 py-3 shadow-md transition-all duration-300 ${
                loading ? 'cursor-not-allowed opacity-70' : ''
              }`}
              style={{ backgroundColor: theme.brand.primary.bg, color: theme.brand.primary.text }}
              onMouseEnter={(e) => {
                if (!loading) e.currentTarget.style.backgroundColor = theme.brand.primary.bgHover
              }}
              onMouseLeave={(e) => {
                if (!loading) e.currentTarget.style.backgroundColor = theme.brand.primary.bg
              }}
            >
              <span className="relative z-10">
                {loading ? (
                  <span className="inline-flex items-center">
                    <svg
                      className="-ml-1 mr-3 h-5 w-5 animate-spin"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Envoi en cours...
                  </span>
                ) : (
                  'Envoyer un email'
                )}
              </span>
            </button>
          </motion.form>
        )}

        {currentStep === 2 && (
          <motion.div
            key="code-step"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            <div className="text-center">
              {codeSentAt && (
                <div className="mb-4 flex justify-center">
                  <CodeTimer
                    duration={5 * 60}
                    onExpire={() => {
                      toast.error('Le code a expiré. Demandez un nouveau code.')
                    }}
                  />
                </div>
              )}

              <CodeInput
                length={6}
                accentColor={theme.brand.gold}
                onComplete={handleCodeComplete}
                loading={loading}
                error={errors.code}
              />
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={() => setCurrentStep(1)}
                className="text-sm transition-colors duration-200"
                style={{ color: textTheme.secondary }}
                onMouseEnter={(e) => { e.currentTarget.style.color = textTheme.primary }}
                onMouseLeave={(e) => { e.currentTarget.style.color = textTheme.secondary }}
              >
                Vous n&apos;avez pas reçu l&apos;email ? Cliquez ici
              </button>
            </div>
          </motion.div>
        )}

        {currentStep === 3 && (
          <motion.form
            key="password-step"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
            onSubmit={handlePasswordSubmit}
          >
            <div className="relative">
              <div
                className={`form-field relative transition-all duration-300 ${errors.newPassword ? 'shake' : ''}`}
              >
                <input
                  id="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  className="w-full rounded-lg border px-4 py-3 pr-12 transition-all duration-300 focus:outline-none"
                  style={{
                    borderColor: getInputBorderColor('newPassword', !!errors.newPassword),
                    backgroundColor: getInputBgColor('newPassword'),
                    color: inputTheme.text,
                  }}
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  onFocus={() => setFocusedInput('newPassword')}
                  onBlur={() => setFocusedInput('')}
                  placeholder=" "
                />
                <label
                  htmlFor="newPassword"
                  className={`pointer-events-none absolute px-1 transition-all duration-300 ${
                    isInputActive('newPassword')
                      ? '-top-2 left-3 z-10 text-xs'
                      : 'left-4 top-3 text-base'
                  }`}
                  style={{
                    color: isInputActive('newPassword') ? theme.brand.accent.text : textTheme.tertiary,
                    // paint only the lower half so the label matches the field below the border
                    background: isInputActive('newPassword') ? `linear-gradient(transparent 50%, ${inputTheme.bgFocus} 50%)` : 'transparent',
                  }}
                >
                  Nouveau mot de passe
                </label>
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors duration-200"
                  style={{ color: textTheme.tertiary }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = textTheme.secondary }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = textTheme.tertiary }}
                  aria-label={
                    showPassword
                      ? 'Masquer le mot de passe'
                      : 'Afficher le mot de passe'
                  }
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
                {errors.newPassword && (
                  <p className="mt-1 text-sm" style={{ color: statusErrorTheme.text }}>
                    {errors.newPassword}
                  </p>
                )}
              </div>

              {/* Password strength indicator */}
              <PasswordStrength
                password={newPassword}
                onStrengthChange={setPasswordStrength}
              />
            </div>

            <div className="relative">
              <div
                className={`form-field relative transition-all duration-300 ${errors.confirmPassword ? 'shake' : ''}`}
              >
                <input
                  id="confirmPassword"
                  type="password"
                  className="w-full rounded-lg border px-4 py-3 transition-all duration-300 focus:outline-none"
                  style={{
                    borderColor: getInputBorderColor('confirmPassword', !!errors.confirmPassword),
                    backgroundColor: getInputBgColor('confirmPassword'),
                    color: inputTheme.text,
                  }}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  onFocus={() => setFocusedInput('confirmPassword')}
                  onBlur={() => setFocusedInput('')}
                  placeholder=" "
                />
                <label
                  htmlFor="confirmPassword"
                  className={`pointer-events-none absolute px-1 transition-all duration-300 ${
                    isInputActive('confirmPassword')
                      ? '-top-2 left-3 z-10 text-xs'
                      : 'left-4 top-3 text-base'
                  }`}
                  style={{
                    color: isInputActive('confirmPassword') ? theme.brand.accent.text : textTheme.tertiary,
                    // paint only the lower half so the label matches the field below the border
                    background: isInputActive('confirmPassword') ? `linear-gradient(transparent 50%, ${inputTheme.bgFocus} 50%)` : 'transparent',
                  }}
                >
                  Confirmer le mot de passe
                </label>
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm" style={{ color: statusErrorTheme.text }}>
                    {errors.confirmPassword}
                  </p>
                )}
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || passwordStrength < 3}
              className={`relative flex w-full justify-center overflow-hidden rounded-lg border border-transparent px-4 py-3 shadow-md transition-all duration-300 ${
                loading ? 'cursor-not-allowed opacity-70' : ''
              }`}
              style={{
                backgroundColor: passwordStrength < 3
                  ? theme.button.disabled.bg
                  : theme.brand.primary.bg,
                color: passwordStrength < 3
                  ? theme.button.disabled.text
                  : theme.brand.primary.text,
                cursor: passwordStrength < 3 ? 'not-allowed' : 'pointer',
              }}
              onMouseEnter={(e) => {
                if (!loading && passwordStrength >= 3) {
                  e.currentTarget.style.backgroundColor = theme.brand.primary.bgHover
                }
              }}
              onMouseLeave={(e) => {
                if (!loading && passwordStrength >= 3) {
                  e.currentTarget.style.backgroundColor = theme.brand.primary.bg
                }
              }}
            >
              <span className="relative z-10">
                {loading ? (
                  <span className="inline-flex items-center">
                    <svg
                      className="-ml-1 mr-3 h-5 w-5 animate-spin"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Modification...
                  </span>
                ) : (
                  'Réinitialiser le mot de passe'
                )}
              </span>
            </button>
          </motion.form>
        )}
      </AnimatePresence>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
          20%, 40%, 60%, 80% { transform: translateX(5px); }
        }
        .shake {
          animation: shake 0.5s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
        }
      `}</style>
    </div>
  )
}

export default ForgotPassword
